import React, { FunctionComponent, useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import MuiTextField from '@material-ui/core/TextField'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { useTranslation } from 'react-i18next'
import { useConfig } from 'components/providers/ConfigProvider'
import { TextField } from 'components/elements/TextField'

export interface MpayLinkDialogData {
  sessionId: number
}

type Props = {
  open: boolean
  data: MpayLinkDialogData | null
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const MpayLinkDialog: FunctionComponent<AllProps> = ({ open, setOpen, data }) => {
  if (!data) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { showSuccess, showError } = useMessage()
  const config = useConfig()

  const [url, setUrl] = useState<string | null>(null)

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    setUrl(null)
    formal.change('days', initialValues.days)
    formal.clearErrors()
  }

  const handleBack = () => {
    setUrl(null)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      showSuccess(t('messages.linkCopied'))
    })
  }

  const initialValues = {
    days: 1,
  }

  const schema = yup.object().shape({
    days: yup.number().required().integer().min(1).max(365),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.getParkingLotSessionToken({
        session_id: data.sessionId,
        expires_in: values.days * 86400,
      })
        .then(response => {
          setUrl(config.mpayUrl + '/parking/' + response.token)
        })
        .catch(() => {
          showError(`${t('alerts.couldNotGenerateMpayLink')} ${t('alerts.tryLater')}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('mpayLink')}</DraggableDialogTitle>
        <DialogContent>
          {!url ? (
            <Grid container>
              <TextField
                GridProps={{ xs: 12 }}
                label={t('validDays')}
                required
                type="number"
                {...formal.getFieldProps('days')}
                autoFocus
                autoComplete="off"
                inputProps={{ min: 1, max: 365 }}
                helperText={t('validDaysHelperText')}
              />
            </Grid>
          ) : (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <MuiTextField
                  multiline
                  fullWidth
                  variant="outlined"
                  value={url}
                  InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.5 } }}
                  autoFocus
                  onFocus={e => e.target.select()}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {t('cancel')}
          </Button>
          {!url ? (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formal.isSubmitting}
            >
              {t('generate')}
            </Button>
          ) : (
            <>
              <Button variant="outlined" color="secondary" onClick={handleBack}>
                {t('back')}
              </Button>
              <Button variant="contained" color="primary" onClick={handleCopy}>
                {t('copy')}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default MpayLinkDialog
